<script setup lang="ts">
import { ref, onMounted } from "vue";
import { Item, ItemPackingPrice } from "../../../types/models";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import AsyncImage from "../form/AsyncImage.vue";
import { formatNumber } from "../../../utils/CommonFunctions";
import IconComponent from "../common/IconComponent.vue";
import QuantityInput from "../form/QuantityInput.vue";
import DocumentHeader from "./DocumentHeader.vue";
import DocumentFooter from "./DocumentFooter.vue";

const props = defineProps<{
  warehouses_id?: number | string | null | undefined;
  price_lists_id?: number | string;
}>();

const productList = ref<Item[] | []>([]);
const selectedProducts = ref<(number | string)[]>([]);

async function getProductList() {
  const params = {
    search_query: code.value,
    price_lists_id: props.price_lists_id,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    productList.value = response.data;
  } else {
    Toast.show("No Items found", "warning");
  }
}

// Toggle selection of a product
const toggleSelection = (productId: string | number) => {
  const index = selectedProducts.value.indexOf(productId);
  if (index > -1) {
    selectedProducts.value.splice(index, 1);
  } else {
    selectedProducts.value.push(productId);
  }
};

// Emit selected products
const emit = defineEmits<{
  (e: "close"): void;
  (e: "selected", items: Item[]): void;
}>();

const submitSelection = () => {
  const selectedItems: Item[] = productList.value.filter(
    (p) => p.code !== undefined && selectedProducts.value.includes(p.code)
  );
  console.log(selectedItems);
  emit("selected", selectedItems);
  emit("close");
};

// Computed property to check if a product is selected
const isSelected = (productId: number | string) =>
  selectedProducts.value.includes(productId);

const updatePackingPrice = (item: Item, packing: ItemPackingPrice) => {
  item.sales_uom_id = packing.unit_of_measures_id;
  item.sales_uom = packing.unit_of_measure;
  item.sales_uom_qty = packing.inventory_qty;
  item.price = packing.selling_price;
  item.showPackingPrices = false;
};

const clearPackingPrice = (item: Item) => {
  item.sales_uom_id = item.default_sales_uom_id;
  item.sales_uom = item.unit_of_measure;
  item.sales_uom_qty = item.default_sales_uom_qty;
  item.price = item.original_price;
  item.showPackingPrices = false;
};

const code = ref("");
onMounted(() => {
  getProductList();
});
</script>

<template>
  <
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]"
  >
    <div
      class="fixed w-full h-full bg-gray-50 dark:bg-gray-900 rounded-lg shadow-xl flex flex-col overflow-hidden max-h-full"
    >
      <DocumentHeader
        title="Select Items"
        end-point="endPoint"
        :readonly="true"
        :show-share-icons="false"
        @close="$emit('close')"
      >
        <template #header>
          <div class="relative">
            <input
              :id="'code_mobile'"
              v-model="code"
              type="text"
              class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
              @keyup.enter="getProductList"
            />
          </div>
        </template>
      </DocumentHeader>
      <!-- header end-->
      <!-- body start -->
      <div class="flex-1 overflow-y-auto">
        <div class="p-2 bg-white dark:bg-gray-800">
          <div
            class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
          >
            <template v-for="product in productList" :key="product.code">
              <div
                :id="'product-box-' + product.code"
                class="relative cursor-pointer bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 border border-gray-300 dark:border-gray-700 hover:shadow-lg transition-all"
                :class="{
                  'ring-2 ring-blue-500':
                    product.code !== undefined && isSelected(product.code),
                }"
                @click="
                  product.code !== undefined && toggleSelection(product.code)
                "
              >
                <!-- Product Image -->
                <div
                  class="w-full flex items-center justify-center overflow-hidden mb-1 relative"
                >
                  <AsyncImage
                    :src="product.photo"
                    :alt="product.name"
                    class-name="w-[50px]"
                  />
                  <div
                    :id="'check-circle-' + product.code"
                    v-if="
                      product.code !== undefined && isSelected(product.code)
                    "
                    class="absolute top-2 right-2 w-6 h-6 flex items-center justify-center border-2 border-gray-400 rounded-full transition-all duration-300 bg-green-700 border-green-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div
                    v-if="
                      product.code !== undefined && isSelected(product.code)
                    "
                    class="absolute top-2 left-2 bg-green-700 text-white text-xs px-2 py-1 rounded"
                  >
                    {{ formatNumber(product.quantity * product.price) }}
                  </div>
                </div>

                <!-- Product Info -->
                <h3 class="text-md font-semibold text-gray-800 dark:text-white">
                  {{ product.name }}
                </h3>
                <div class="flex justify-between items-center">
                  <p class="text-gray-500 dark:text-gray-400 text-sm flex-grow">
                    Code: {{ product.code }}
                  </p>
                  <div
                    class="max-w-[120px] ml-2 flex items-center"
                    v-if="
                      product.code !== undefined && isSelected(product.code)
                    "
                    @click.stop
                  >
                    <button
                      v-if="
                        product.packing_prices &&
                        product.packing_prices.length > 0
                      "
                      @click.stop="
                        product.showPackingPrices = !product.showPackingPrices
                      "
                      class="text-xs text-gray-600 dark:text-gray-400 hover:text-blue-700 dark:hover:text-blue-300 mr-2"
                      title="Packing Prices"
                    >
                      <IconComponent name="List" />
                    </button>
                    <div
                      v-if="product.showPackingPrices"
                      class="absolute bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 mt-1 z-10"
                    >
                      <div
                        v-for="price in (product.packing_prices ?? []).filter(
                          (e) => e.price_lists_id == props.price_lists_id
                        )"
                        :key="price.uid"
                        class="flex justify-between text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                        @click="updatePackingPrice(product, price)"
                      >
                        <span
                          >{{ price.unit_of_measure }} x
                          {{ price.inventory_qty }}</span
                        >
                        <span class="font-bold">{{
                          formatNumber(price.selling_price)
                        }}</span>
                      </div>
                    </div>
                    <QuantityInput v-model="product.quantity" />
                  </div>
                </div>
                <div
                  class="flex justify-between text-sm text-gray-700 dark:text-gray-300"
                >
                  <div class="flex w-full items-center">
                    <span class="text-gray-500 dark:text-gray-400 text-sm"
                      >Price:
                      <span
                        v-if="product.default_sales_uom_id != product.sales_uom_id"
                      >
                        ({{ product.sales_uom }} x
                        {{ product.sales_uom_qty }})</span
                      ></span
                    >
                    <button
                      v-if="product.sales_uom_id != product.default_sales_uom_id"
                      @click="clearPackingPrice(product)"
                      class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 h-6 flex items-center"
                      title="Clear"
                    >
                      <IconComponent name="Trash2" />
                    </button>
                  </div>
                  <span class="font-bold">{{
                    formatNumber(product.price)
                  }}</span>
                </div>

                <!-- Prices -->
                <div
                  class="mt-2"
                  v-if="
                    (product.packing_prices ?? []).filter(
                      (e) => e.price_lists_id == props.price_lists_id
                    ).length > 0
                  "
                >
                  <div
                    v-for="price in product.packing_prices
                      ?.filter((e) => e.price_lists_id == props.price_lists_id)
                      .splice(0, 2)"
                    :key="price.uid"
                    class="flex justify-between text-sm text-gray-700 dark:text-gray-300"
                  >
                    <span
                      >{{ price.unit_of_measure }} x
                      {{ price.inventory_qty }}</span
                    >
                    <span class="font-bold">{{
                      formatNumber(price.selling_price)
                    }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- body end -->
      <!-- footer start -->
      <DocumentFooter
        :show-text="false"
        :show-share-icons="false"
        :show-new-icon="false"
        :readonly="true"
        end-point="endPoint"
        title="Select Items"
        @close="$emit('close')"
      >
        <template #all-buttons>
          <button
            @click="submitSelection"
            v-if="selectedProducts.length > 0"
            eventtype="info"
            id="select-products"
            class="px-2 md:px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 flex items-center gap-2"
          >
            <IconComponent name="CircleArrowOutDownRight" />
            ADD ITEMS
          </button>
        </template>
      </DocumentFooter>
      <!-- footer end -->
    </div>
  </div>
  >
</template>
